// Shared colour-mapping helpers for elevation-derived analytical layers.
// Used by ElevationProfile.tsx (the cross-section chart) and by usePaths.ts
// (per-path on-map polyline coloring). Single source of truth for slope /
// aspect / geomorphons / TWI palettes — palettes match the backend's
// classification colormaps (web-server/ops/dem_processing/visualization/
// colormaps.go) so chart, on-map raster, and on-map path lines all agree.
// Slope (10 canonical bands, NewSlopeClassification).
export const SLOPE_BANDS = [
    { max: 2, color: '#d9f0a3', label: 'Flat · 0–2° (0–3%)' },
    { max: 4, color: '#addd8e', label: 'Gentle · 2–4° (3–7%)' },
    { max: 8, color: '#78c679', label: 'Moderate · 4–8° (7–14%)' },
    { max: 12, color: '#31a354', label: 'Strong · 8–12° (14–21%)' },
    { max: 18, color: '#fff1a1', label: 'Mod. Steep · 12–18° (21–32%)' },
    { max: 24, color: '#fed976', label: 'Steep · 18–24° (32–45%)' },
    { max: 30, color: '#feb24c', label: 'Very Steep · 24–30° (45–58%)' },
    { max: 36, color: '#fd8d3c', label: 'Extremely Steep · 30–36° (58–73%)' },
    { max: 45, color: '#e31a1c', label: 'Excessively Steep · 36–45° (73–100%)' },
    { max: 90, color: '#bd0026', label: 'Hazardously Steep · 45°+ (100%+)' },
];
export const slopeToColor = (slopeDeg) => {
    const abs = Math.abs(slopeDeg);
    for (const band of SLOPE_BANDS) {
        if (abs <= band.max)
            return band.color;
    }
    return SLOPE_BANDS[SLOPE_BANDS.length - 1].color;
};
export const ASPECT_BANDS_NORTH = [
    { label: 'North', short: 'N', color: '#000080', min: 337.5, max: 22.5 }, // wraps
    { label: 'Northeast', short: 'NE', color: '#4169e1', min: 22.5, max: 67.5 },
    { label: 'East', short: 'E', color: '#008000', min: 67.5, max: 112.5 },
    { label: 'Southeast', short: 'SE', color: '#ffff00', min: 112.5, max: 157.5 },
    { label: 'South', short: 'S', color: '#ffa500', min: 157.5, max: 202.5 },
    { label: 'Southwest', short: 'SW', color: '#ff0000', min: 202.5, max: 247.5 },
    { label: 'West', short: 'W', color: '#800080', min: 247.5, max: 292.5 },
    { label: 'Northwest', short: 'NW', color: '#89cff0', min: 292.5, max: 337.5 },
];
export const ASPECT_BANDS_SOUTH = [
    { label: 'North', short: 'N', color: '#ffa500', min: 337.5, max: 22.5 },
    { label: 'Northeast', short: 'NE', color: '#ffff00', min: 22.5, max: 67.5 },
    { label: 'East', short: 'E', color: '#008000', min: 67.5, max: 112.5 },
    { label: 'Southeast', short: 'SE', color: '#4169e1', min: 112.5, max: 157.5 },
    { label: 'South', short: 'S', color: '#000080', min: 157.5, max: 202.5 },
    { label: 'Southwest', short: 'SW', color: '#89cff0', min: 202.5, max: 247.5 },
    { label: 'West', short: 'W', color: '#800080', min: 247.5, max: 292.5 },
    { label: 'Northwest', short: 'NW', color: '#ff0000', min: 292.5, max: 337.5 },
];
export const aspectBandsFor = (h) => h === 'S' ? ASPECT_BANDS_SOUTH : ASPECT_BANDS_NORTH;
const aspectBandIndex = (bearingDeg) => {
    const b = ((bearingDeg % 360) + 360) % 360;
    if (b >= 337.5 || b < 22.5)
        return 0;
    return Math.min(7, Math.floor((b - 22.5) / 45) + 1);
};
export const aspectToColor = (bearingDeg, hemisphere) => aspectBandsFor(hemisphere)[aspectBandIndex(bearingDeg)].color;
export const bearingShort = (bearingDeg) => ASPECT_BANDS_NORTH[aspectBandIndex(bearingDeg)].short;
export const GEOMORPHON_CLASSES = [
    { id: 1, label: 'Flat', color: '#e6e6e6' },
    { id: 2, label: 'Peak', color: '#79553d' },
    { id: 3, label: 'Ridge', color: '#a47449' },
    { id: 4, label: 'Shoulder', color: '#bc9772' },
    { id: 5, label: 'Spur', color: '#d5c4a1' },
    { id: 6, label: 'Slope', color: '#b0b0b0' },
    { id: 7, label: 'Hollow', color: '#a4c4a8' },
    { id: 8, label: 'Footslope', color: '#96b4be' },
    { id: 9, label: 'Valley', color: '#6e9eab' },
    { id: 10, label: 'Pit', color: '#466166' },
];
const geomorphonByID = (classID) => {
    var _a;
    const id = Math.round(classID);
    return (_a = GEOMORPHON_CLASSES.find(c => c.id === id)) !== null && _a !== void 0 ? _a : null;
};
export const geomorphonToColor = (classID) => { var _a, _b; return (_b = (_a = geomorphonByID(classID)) === null || _a === void 0 ? void 0 : _a.color) !== null && _b !== void 0 ? _b : '#888'; };
export const geomorphonLabel = (classID) => { var _a, _b; return (_b = (_a = geomorphonByID(classID)) === null || _a === void 0 ? void 0 : _a.label) !== null && _b !== void 0 ? _b : '—'; };
// TWI Blues colormap, normalised across a min/max range.
export const TWI_LOW_RGB = [240, 248, 255]; // light/dry
export const TWI_HIGH_RGB = [8, 81, 156]; // navy/wet
export const twiToColor = (value, min, max) => {
    const range = max - min;
    const t = range > 0 ? Math.max(0, Math.min(1, (value - min) / range)) : 0;
    const r = Math.round(TWI_LOW_RGB[0] * (1 - t) + TWI_HIGH_RGB[0] * t);
    const g = Math.round(TWI_LOW_RGB[1] * (1 - t) + TWI_HIGH_RGB[1] * t);
    const b = Math.round(TWI_LOW_RGB[2] * (1 - t) + TWI_HIGH_RGB[2] * t);
    return `rgb(${r}, ${g}, ${b})`;
};
// Flow accumulation — same Blues palette as TWI, but log-transformed before
// normalising. Mirrors the backend's generateBluesRGBA-after-log1p path used
// to render the on-map flow_accumulation raster. Raw values are upstream-cell
// counts (can range from 1 to many millions), so log1p compresses the range
// into something visually useful.
export const flowAccumulationToColor = (value, min, max) => {
    const lv = Math.log1p(Math.max(0, value));
    const lo = Math.log1p(Math.max(0, min));
    const hi = Math.log1p(Math.max(0, max));
    const range = hi - lo;
    const t = range > 0 ? Math.max(0, Math.min(1, (lv - lo) / range)) : 0;
    const r = Math.round(TWI_LOW_RGB[0] * (1 - t) + TWI_HIGH_RGB[0] * t);
    const g = Math.round(TWI_LOW_RGB[1] * (1 - t) + TWI_HIGH_RGB[1] * t);
    const b = Math.round(TWI_LOW_RGB[2] * (1 - t) + TWI_HIGH_RGB[2] * t);
    return `rgb(${r}, ${g}, ${b})`;
};
export const rgbHex = (rgb) => '#' + rgb.map(c => c.toString(16).padStart(2, '0')).join('');
// Compute the colour for a single elevation profile sample given the active
// colour-by mode + the path's hemisphere + (for TWI) the raster-wide range.
// Returns null when the sample doesn't have a value for the requested layer
// (caller should fall back to the path's base colour). Note: 'slope_grade'
// requires two samples — use pairColor for that mode; sampleColor returns null.
export const sampleColor = (sample, mode, hemisphere, twiRange) => {
    if (mode === 'elevation' || mode === 'slope_grade')
        return null;
    const lv = sample.layerValues || {};
    switch (mode) {
        case 'slope': {
            const v = lv.slope;
            return v && v.valid ? slopeToColor(v.value) : null;
        }
        case 'aspect': {
            const v = lv.aspect;
            return v && v.valid ? aspectToColor(v.value, hemisphere) : null;
        }
        case 'geomorphons': {
            const v = lv.geomorphons;
            return v && v.valid ? geomorphonToColor(v.value) : null;
        }
        case 'twi': {
            const v = lv.twi;
            if (!(v && v.valid) || !twiRange)
                return null;
            return twiToColor(v.value, twiRange.min, twiRange.max);
        }
        // flow_accumulation lives on its own range, handled by the chart with
        // a separate min/max — keep it out of sampleColor's signature so we
        // don't inflate the prop list. Chart calls flowAccumulationToColor
        // directly.
        case 'flow_accumulation':
            return null;
    }
    return null;
};
// Path-aligned grade between two consecutive elevation samples, in degrees.
// rise / run from the elevations + horizontal distances. Returns null when
// either sample is invalid or the run is zero.
export const pathGradeBetween = (s0, s1) => {
    if (!s0.valid || !s1.valid)
        return null;
    const dh = s1.distanceMeters - s0.distanceMeters;
    if (dh <= 0)
        return null;
    const dz = s1.elevationMeters - s0.elevationMeters;
    return Math.atan2(dz, dh) * (180 / Math.PI);
};
// Color for a sample-pair. Used by the on-map polyline overlay where each
// rendered piece spans two consecutive samples — the pair-form is what
// 'slope_grade' (path grade) needs. For all other modes, defers to
// sampleColor on s0; the second sample is unused.
export const pairColor = (s0, s1, mode, hemisphere, twiRange) => {
    if (mode === 'slope_grade') {
        const g = pathGradeBetween(s0, s1);
        return g === null ? null : slopeToColor(g);
    }
    return sampleColor(s0, mode, hemisphere, twiRange);
};
